import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import ThemeMaker from "../../components/thememaker";
import { config } from "../../utils/config";
import { tokenDecode } from "../../utils/helper";
import { useHistory, useParams } from "react-router-dom";
import axios from "axios";

const DESCRIPTORS = [
    { group: "Identifier", code: "Identifier", short: "GSTIN", def: "GST number" },
    { group: "Identifier", code: "Identifier", short: "PAN", def: "PAN number" },
    { group: "Identifier", code: "Identifier", short: "Regno", def: "Shop registration number" },
    { group: "Identifier", code: "Identifier", short: "FSSAI", def: "FSSAI number" },
    { group: "Identifier", code: "Identifier", short: "FPO", def: "FPO identifier" },
    { group: "Identifier", code: "Identifier", short: "Udyam", def: "Udyam number" },
    { group: "Social Sector", code: "Social Sector", short: "SHG", def: "Seller is self-help-group" },
    { group: "Social Sector", code: "Social Sector", short: "FPO", def: "Seller is FPO" },
    { group: "Social Sector", code: "Social Sector", short: "Food Processing Micro/Nano Enterprise", def: "Micro/nano food-processing enterprise" },
    { group: "Social Sector", code: "Social Sector", short: "Handicraft Micro/Nano Enterprise", def: "Micro/nano handicrafts enterprise" },
    { group: "Social Sector", code: "Social Sector", short: "Handloom Micro/Nano Enterprise", def: "Micro/nano handlooms enterprise" },
    { group: "Social Sector", code: "Social Sector", short: "OFPO", def: "Off-farm producer organization" },
    { group: "Social Sector", code: "Social Sector", short: "Silkmark", def: "Silkmark-certified products" },
    { group: "Social Sector", code: "Social Sector", short: "Craftmark", def: "Craftmark-certified products" },
    { group: "Social Sector", code: "Social Sector", short: "GI", def: "GI-tag-certified products" },
    { group: "Social Sector", code: "Social Sector", short: "ODOP", def: "ODOP-certified products" },
    { group: "Social Sector", code: "Social Sector", short: "Fair Trade", def: "Fair Trade certified seller" },
    { group: "Social Sector", code: "Social Sector", short: "Organic", def: "Certified organic products" },
    { group: "Social Sector", code: "Social Sector", short: "Agmark", def: "Agmark-certified products" },
    { group: "Social Sector", code: "Social Sector", short: "FPO Mark", def: "FPO Mark certification" },
    { group: "Social Sector", code: "Social Sector", short: "Handloom Mark", def: "Handloom Mark-certified products" },
    { group: "Social Sector", code: "Social Sector", short: "Khadi India Mark", def: "Khadi India Mark-certified products" },
    { group: "Social Sector", code: "Social Sector", short: "Women Owned Business", def: "Women-owned business" },
    { group: "Social Sector", code: "Social Sector", short: "Tribal Owned Business", def: "Tribal-owned business" },
    { group: "Social Sector", code: "Social Sector", short: "Aspirational District/Block Based Business", def: "Located in an aspirational district/block" },
    { group: "Social Sector", code: "Social Sector", short: "Eco Friendly/Sustainable", def: "Eco-friendly/sustainable products" },
    { group: "Social Sector", code: "Social Sector", short: "MSME TEAM", def: "MSME under the TEAM initiative" },
];

const apiUrl = config.baseNodeapiUrl

function uuidv4() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function todayISO() {
    return new Date().toISOString().slice(0, 10);
}
function plusOneYearISO(dateStr) {
    const d = new Date(dateStr);
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
}

const uploadApiUrl = "https://prod-sellerapp.shiprocket.com/shiprocket/upload";

async function uploadFile(file) {
    const formData = new FormData();
    formData.append("file", file);

    const res = await fetch(uploadApiUrl, {
        method: "POST",
        body: formData,
    });

    if (!res.ok) {
        let detail = "";
        try {
            const j = await res.json();
            detail = j.message || JSON.stringify(j);
        } catch (_) { }
        throw new Error(`Upload failed (${res.status})${detail ? ": " + detail : ""}`);
    }

    const data = await res.json();

    if (!data?.success) {
        throw new Error(data?.message || "Upload failed.");
    }

    const url = Array.isArray(data?.urls) ? data.urls[0] : undefined;

    if (!url) {
        throw new Error("Upload succeeded but no file URL was returned.");
    }
    return url;
}

const emptyForm = () => ({
    providerId: "",
    descriptorIdx: "",
    descriptorName: "",
    url: "",
    validFrom: "",
    validTo: "",
    isActive: true,
});

export default function ApprovedEnum() {
    const params = useParams();
    const id = params?.id || (() => {
        const segments = window.location.pathname.split("/").filter(Boolean);
        const last = segments[segments.length - 1];
        return last && last !== "approved-enums" ? last : undefined;
    })();
    const isEditMode = Boolean(id);

    const [credentialId, setCredentialId] = useState(uuidv4());
    const [form, setForm] = useState(emptyForm());
    const [autoFilledName, setAutoFilledName] = useState("");
    const [errors, setErrors] = useState({});
    const [providers, setProviders] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [banner, setBanner] = useState(null); // { ok: bool, message: string }
    const [logoFileName, setLogoFileName] = useState("");
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [loadingRecord, setLoadingRecord] = useState(false);
    const nameManuallyEdited = useRef(false);
    const [sellerList, setSellerList] = useState([]);
    const baseUrl = config.apiEndpoint;

    // ---- Determine login type (admin vs seller) from token ----
    // seller_id is NOT reliable here — it's just the seller's own record id
    // and varies per account (e.g. 88, 5, whatever). role_id is the actual
    // role flag: 1 = admin, 2 = seller. (parent_id === 0 for admin vs
    // non-zero for seller would also work as a fallback.)
    const decodedToken = useMemo(() => {
        try {
            const raw = sessionStorage.getItem('us_st_d');
            return raw ? tokenDecode(raw) : null;
        } catch (e) {
            console.log("token decode error", e);
            return null;
        }
    }, []);

    const isSellerLogin = decodedToken?.data?.user?.role_id === 2;
    const tokenProviderId = decodedToken?.data?.seller_data?.provider_id || "";
    console.log("isSellerLoginisSellerLogin", isSellerLogin);

    const fetchSellerList = async () => {
        try {
            const { data } = await axios({
                url: `${baseUrl}/getsellerlist`,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`
                }
            });

            if (data.status === 200) {
                setSellerList(data.data);
            }
        } catch (error) {
            console.log("error", error);
        }
    }

    useEffect(() => {
        if (!isSellerLogin) {
            fetchSellerList();
        }
        if (!id) return;

        let cancelled = false;

        const fetchRecord = async () => {
            setLoadingRecord(true);
            setBanner(null);
            try {
                const res = await fetch(`${apiUrl}/admin/api/social-sector-providers/${id}`, {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                });
                if (!res.ok) {
                    throw new Error(`Failed to load provider (${res.status})`);
                }
                const json = await res.json();
                const record = json?.data;
                if (!record) {
                    throw new Error("Provider record not found.");
                }
                if (cancelled) return;

                const descriptorIdx = DESCRIPTORS.findIndex(
                    (d) =>
                        d.code === record.descriptor_code &&
                        d.short === record.descriptor_short_desc
                );

                setCredentialId(record.credential_id || uuidv4());
                setForm({
                    providerId: record.provider_id || "",
                    descriptorIdx: descriptorIdx >= 0 ? String(descriptorIdx) : "",
                    descriptorName: record.descriptor_name || "",
                    url: record.url || "",
                    validFrom: record.valid_from || todayISO(),
                    validTo: record.valid_to || plusOneYearISO(todayISO()),
                    isActive: Boolean(record.is_active),
                });
                setAutoFilledName(record.descriptor_short_desc || "");
                nameManuallyEdited.current = true;
                setLogoFileName("");
            } catch (err) {
                if (!cancelled) {
                    setBanner({ ok: false, message: err.message || "Could not load the provider for editing." });
                }
            } finally {
                if (!cancelled) setLoadingRecord(false);
            }
        };

        fetchRecord();
        return () => {
            cancelled = true;
        };
    }, [id, isSellerLogin]);

    useEffect(() => {
        if (isSellerLogin && tokenProviderId && !isEditMode) {
            setForm((f) => (f.providerId ? f : { ...f, providerId: tokenProviderId }));
        }
    }, [isSellerLogin, tokenProviderId, isEditMode]);

    const selectedDescriptor = useMemo(() => {
        return form.descriptorIdx === "" ? null : DESCRIPTORS[Number(form.descriptorIdx)];
    }, [form.descriptorIdx]);

    const groupedOptions = useMemo(() => {
        const groups = {};
        DESCRIPTORS.forEach((d, idx) => {
            if (!groups[d.group]) groups[d.group] = [];
            groups[d.group].push({ idx, ...d });
        });
        return groups;
    }, []);

    const payload = useMemo(
        () => ({
            provider_id: form.providerId.trim(),
            credential_id: credentialId,
            descriptor_code: selectedDescriptor ? selectedDescriptor.code : "",
            descriptor_short_desc: selectedDescriptor ? selectedDescriptor.short : "",
            descriptor_name: form.descriptorName.trim(),
            url: form.url.trim(),
            verify_url: "",
            valid_from: form.validFrom,
            valid_to: form.validTo,
            is_active: form.isActive,
        }),
        [credentialId, form, selectedDescriptor]
    );

    const field = useCallback((key, value) => {
        setForm((f) => ({ ...f, [key]: value }));
    }, []);

    const handleValidFromChange = (value) => {
        setForm((f) => ({
            ...f,
            validFrom: value,
            validTo: value ? plusOneYearISO(value) : f.validTo,
        }));
    };

    const handleProviderChange = (value) => {
        setErrors((e) => ({ ...e, providerId: false, providerDup: false }));
        field("providerId", value);
    };

    const handleDescriptorChange = (idxStr) => {
        setErrors((e) => ({ ...e, descriptor: false }));
        setForm((f) => {
            let nextName = f.descriptorName;
            if (idxStr !== "") {
                const d = DESCRIPTORS[Number(idxStr)];
                if (!nameManuallyEdited.current || f.descriptorName.trim() === autoFilledName) {
                    nextName = d.short;
                    setAutoFilledName(d.short);
                    nameManuallyEdited.current = false;
                }
            }
            return { ...f, descriptorIdx: idxStr, descriptorName: nextName };
        });
    };

    const handleNameChange = (value) => {
        nameManuallyEdited.current = true;
        field("descriptorName", value);
    };

    const handleLogoFileChange = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setErrors((er) => ({ ...er, url: false }));
        setBanner(null);
        setUploadingLogo(true);
        try {
            const uploadedUrl = await uploadFile(file);
            field("url", uploadedUrl);
            setLogoFileName(file.name);
        } catch (err) {
            field("url", "");
            setLogoFileName("");
            setBanner({ ok: false, message: err.message || "Could not upload the selected image." });
        } finally {
            setUploadingLogo(false);
        }
    };

    const validate = () => {
        const e = {};
        if (!form.providerId.trim()) e.providerId = true;
        if (form.descriptorIdx === "") e.descriptor = true;
        if (!form.descriptorName.trim()) e.descriptorName = true;
        if (!form.url.trim()) e.url = true;
        if (!form.validFrom) e.validFrom = true;
        if (!form.validTo) e.validTo = true;
        if (
            !isEditMode &&
            form.providerId.trim() &&
            providers.some((p) => p.provider_id === form.providerId.trim())
        ) {
            e.providerId = true;
            e.providerDup = true;
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const resetForm = (keepBanner = false) => {
        setCredentialId(uuidv4());
        setForm({ ...emptyForm(), providerId: isSellerLogin ? tokenProviderId : "" });
        setAutoFilledName("");
        setLogoFileName("");
        setUploadingLogo(false);
        nameManuallyEdited.current = false;
        setErrors({});
        if (!keepBanner) setBanner(null);
    };

    const history = useHistory();
    const handleSubmit = async (evt) => {
        evt.preventDefault();
        if (!validate()) {
            const isDuplicate =
                !isEditMode &&
                form.providerId.trim() &&
                providers.some((p) => p.provider_id === form.providerId.trim());

            if (!form.providerId.trim()) {
                setBanner({
                    ok: false,
                    message: "Please select a provider before submitting.",
                });
            } else if (isDuplicate) {
                setBanner({ ok: false, message: "This provider ID has already been added." });
            } else {
                setBanner({ ok: false, message: "Please fix the highlighted fields before submitting." });
            }
            return;
        }
        setSubmitting(true);
        setBanner(null);
        try {
            const url = isEditMode
                ? `${apiUrl}/admin/api/social-sector-providers/${id}`
                : `${apiUrl}/admin/api/social-sector-providers`;
            const res = await fetch(url, {
                method: isEditMode ? "PUT" : "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                let detail = "";
                try {
                    const j = await res.json();
                    detail = j.message || JSON.stringify(j);
                } catch (_) { }
                throw new Error(`Request failed (${res.status})${detail ? ": " + detail : ""}`);
            }
            setProviders((list) => [...list, payload]);
            setBanner({
                ok: true,
                message: isEditMode
                    ? `Provider "${payload.provider_id}" updated successfully.`
                    : `Provider "${payload.provider_id}" added successfully.`,
            });
            if (!isEditMode) resetForm(true);
            history.push("/credentials-list");
        } catch (err) {
            setBanner({ ok: false, message: err.message || "Something went wrong while submitting." });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ThemeMaker>
            <div style={S.app}>
                <header style={S.top}>
                    <div style={S.brand}>
                        <div style={S.mark}>SSP</div>
                        <div>
                            <h1 style={S.h1}>{isEditMode ? "Edit Social Sector Provider" : "Social Sector Providers"}</h1>
                            <p style={S.subtext}>
                                {isEditMode ? "Update the credential descriptor for this provider" : "Register a credential descriptor for a provider"}
                            </p>
                        </div>
                    </div>
                </header>

                <div style={S.grid}>
                    <section style={S.panel}>
                        {loadingRecord ? (
                            <div style={S.emptyState}>Loading provider…</div>
                        ) : (
                            <form onSubmit={handleSubmit} noValidate>

                                <Field label="Provider Id *" invalid={errors.providerId}>
                                    {isSellerLogin ? (
                                        <div style={S.monoReadonly}>
                                            <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: COLORS.textDim }}>
                                                {form.providerId || "—"}
                                            </span>
                                        </div>
                                    ) : (
                                        <SearchableSelect
                                            value={form.providerId}
                                            onChange={(v) => handleProviderChange(v)}
                                            options={sellerList}
                                            placeholder="Select a provider…"
                                            invalid={errors.providerId}
                                            getValue={(s) => s.provider_id}
                                            getLabel={(s) => `${s.company_name || s.name} (${s.provider_id})`}
                                        />
                                    )}
                                    {errors.providerDup && (
                                        <div style={S.errorText}>This provider ID has already been added.</div>
                                    )}
                                </Field>

                                <Field label="Short description *" invalid={errors.descriptor}>
                                    <select
                                        value={form.descriptorIdx}
                                        onChange={(e) => handleDescriptorChange(e.target.value)}
                                        style={{ ...S.input, ...(errors.descriptor ? S.inputInvalid : {}), cursor: "pointer" }}>
                                        <option value="" disabled>
                                            Select a descriptor…
                                        </option>
                                        {Object.entries(groupedOptions).map(([groupName, items]) => (
                                            <optgroup label={groupName} key={groupName}>
                                                {items.map((item) => (
                                                    <option value={item.idx} key={item.idx}>
                                                        {item.short}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>
                                </Field>

                                <Field label="Descriptor code">
                                    <div style={S.monoReadonly}>
                                        <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: COLORS.textDim }}>
                                            {selectedDescriptor ? selectedDescriptor.code : "—"}
                                        </span>
                                    </div>
                                </Field>

                                <Field label="Definition">
                                    <div style={S.note}>
                                        {selectedDescriptor ? selectedDescriptor.def : "Select a short description above to see its definition."}
                                    </div>
                                </Field>

                                <Field label="Descriptor name *" invalid={errors.descriptorName}>
                                    <input
                                        type="text"
                                        placeholder="e.g. MSME TEAM"
                                        value={form.descriptorName}
                                        onChange={(e) => handleNameChange(e.target.value)}
                                        style={{ ...S.input, ...(errors.descriptorName ? S.inputInvalid : {}) }} />
                                </Field>

                                <Field label="Logo / icon *" invalid={errors.url}>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        disabled={uploadingLogo}
                                        onChange={handleLogoFileChange}
                                        style={{ ...S.input, ...(errors.url ? S.inputInvalid : {}), padding: "7px 11px" }} />
                                    {uploadingLogo && (
                                        <div style={{ fontSize: 11.5, color: COLORS.textDim, marginTop: 6 }}>
                                            Uploading…
                                        </div>
                                    )}
                                    {!uploadingLogo && form.url.trim() && (
                                        <div style={S.previewThumb}>
                                            <img src={form.url} alt="" style={S.previewImg} />
                                            <span>{logoFileName || "Current image"}</span>
                                        </div>
                                    )}
                                </Field>

                                <div style={S.row2}>
                                    <Field label="Valid from *" invalid={errors.validFrom}>
                                        <input
                                            type="date"
                                            value={form.validFrom}
                                            onChange={(e) => handleValidFromChange(e.target.value)}
                                            style={{ ...S.input, ...(errors.validFrom ? S.inputInvalid : {}) }}
                                        />
                                    </Field>
                                    <Field label="Valid to *" invalid={errors.validTo}>
                                        <input
                                            type="date"
                                            value={form.validTo}
                                            onChange={(e) => field("validTo", e.target.value)}
                                            style={{ ...S.input, ...(errors.validTo ? S.inputInvalid : {}) }}
                                        />
                                    </Field>
                                </div>

                                <div style={S.toggleRow}>
                                    <div>
                                        <strong style={{ fontSize: 13, display: "block" }}>Active</strong>
                                        <span style={{ fontSize: 11.5, color: COLORS.textDim }}>
                                            Provider is usable immediately if on
                                        </span>
                                    </div>
                                    <Switch checked={form.isActive} onChange={(v) => field("isActive", v)} />
                                </div>

                                {banner && (
                                    <div style={{ ...S.banner, ...(banner.ok ? S.bannerOk : S.bannerFail) }}>
                                        {banner.message}
                                    </div>
                                )}

                                <div style={S.actions}>
                                    <button type="submit" disabled={submitting || uploadingLogo} style={S.primaryBtn}>
                                        {submitting ? (isEditMode ? "Updating…" : "Adding…") : (isEditMode ? "Update provider" : "Add provider")}
                                    </button>
                                </div>
                            </form>
                        )}
                    </section>
                </div>
            </div>
        </ThemeMaker>
    );
}

function Field({ label, hint, invalid, children }) {
    return (
        <div style={{ marginBottom: 14 }}>
            <label style={S.label}>
                <span>{label}</span>
                {hint && <span style={S.hint}>{hint}</span>}
            </label>
            {children}
        </div>
    );
}

function SearchableSelect({ value, onChange, options, placeholder, invalid, getLabel, getValue }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const wrapRef = useRef(null);
    const inputRef = useRef(null);

    const selected = options.find((o) => getValue(o) === value);

    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setQuery("");
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => getLabel(o).toLowerCase().includes(q));
    }, [query, options, getLabel]);

    return (
        <div ref={wrapRef} style={{ position: "relative" }}>
            <div
                onClick={() => {
                    setOpen(true);
                    setTimeout(() => inputRef.current?.focus(), 0);
                }}
                style={{
                    ...S.input,
                    ...(invalid ? S.inputInvalid : {}),
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                }}
            >
                {open ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={selected ? getLabel(selected) : placeholder}
                        style={{
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            fontSize: 13,
                            fontFamily: FONT_SANS,
                            width: "100%",
                            padding: 0,
                            color: COLORS.text,
                        }}
                    />
                ) : (
                    <span style={{ color: selected ? COLORS.text : COLORS.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {selected ? getLabel(selected) : placeholder}
                    </span>
                )}
                <span style={{ color: COLORS.textDim, fontSize: 11, flexShrink: 0 }}>▾</span>
            </div>

            {open && (
                <div
                    style={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        left: 0,
                        right: 0,
                        maxHeight: 220,
                        overflowY: "auto",
                        background: COLORS.panel,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: 7,
                        boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                        zIndex: 20,
                    }}
                >
                    {filtered.length === 0 && (
                        <div style={{ padding: "9px 11px", fontSize: 12.5, color: COLORS.textDim }}>No matches</div>
                    )}
                    {filtered.map((o) => {
                        const v = getValue(o);
                        return (
                            <div
                                key={v}
                                onClick={() => {
                                    onChange(v);
                                    setOpen(false);
                                    setQuery("");
                                }}
                                style={{
                                    padding: "9px 11px",
                                    fontSize: 12.5,
                                    cursor: "pointer",
                                    background: v === value ? COLORS.tealLight : "transparent",
                                    color: v === value ? "#0B5B4F" : COLORS.text,
                                }}
                                onMouseEnter={(e) => {
                                    if (v !== value) e.currentTarget.style.background = "#F5F6F8";
                                }}
                                onMouseLeave={(e) => {
                                    if (v !== value) e.currentTarget.style.background = "transparent";
                                }}
                            >
                                {getLabel(o)}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function Switch({ checked, onChange }) {
    return (
        <label style={{ position: "relative", width: 40, height: 22, display: "inline-block", flexShrink: 0 }}>
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span
                style={{
                    position: "absolute",
                    inset: 0,
                    background: checked ? COLORS.teal : "#C7CCD3",
                    borderRadius: 999,
                    transition: "0.15s",
                    cursor: "pointer",
                }}
                onClick={() => onChange(!checked)}
            >
                <span
                    style={{
                        position: "absolute",
                        height: 16,
                        width: 16,
                        left: checked ? 21 : 3,
                        top: 3,
                        background: "#fff",
                        borderRadius: "50%",
                        transition: "0.15s",
                    }}
                />
            </span>
        </label>
    );
}

const FONT_MONO = "'IBM Plex Mono', ui-monospace, monospace";
const FONT_SANS = "'IBM Plex Sans', system-ui, sans-serif";

const COLORS = {
    navy: "#0F2540",
    navy2: "#16324F",
    teal: "#0F7B6C",
    tealLight: "#E4F3EF",
    bg: "#F5F6F8",
    panel: "#FFFFFF",
    border: "#E1E4EA",
    text: "#1B2430",
    textDim: "#5B6472",
    danger: "#B3261E",
    dangerLight: "#FBEAE9",
};

const S = {
    app: {
        fontFamily: FONT_SANS,
        background: COLORS.bg,
        color: COLORS.text,
        height: "75vh",
        overflow: 'scroll',
        padding: "0px 30px",
        maxWidth: 1180,
        margin: "0 auto",
        boxSizing: "border-box",
    },
    top: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 24,
        marginBottom: 28,
        flexWrap: "wrap",
    },
    brand: { display: "flex", alignItems: "center", gap: 14, marginTop: "30px" },
    mark: {
        width: 44,
        height: 44,
        borderRadius: 9,
        background: COLORS.navy,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontFamily: FONT_MONO,
        fontWeight: 600,
        fontSize: 13,
        flexShrink: 0,
    },
    h1: { fontSize: 19, margin: 0, fontWeight: 700, letterSpacing: "-0.2px" },
    subtext: { margin: "2px 0 0", fontSize: 13, color: COLORS.textDim },
    endpointConfig: {
        background: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 10,
        padding: "12px 16px",
        minWidth: 320,
        flex: 1,
        maxWidth: 460,
        boxSizing: "border-box",
    },
    endpointSuffix: {
        fontFamily: FONT_MONO,
        fontSize: 11,
        color: COLORS.textDim,
        marginTop: 6,
        wordBreak: "break-all",
    },

    panel: {
        background: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 10,
        padding: 22,
        boxSizing: "border-box",
    },
    h2: { fontSize: 15, margin: "0 0 4px", fontWeight: 700 },
    panelSub: { fontSize: 12.5, color: COLORS.textDim, margin: "0 0 18px" },
    label: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        fontSize: 12.5,
        fontWeight: 600,
        marginBottom: 6,
    },
    hint: { fontWeight: 400, color: COLORS.textDim, fontSize: 11 },
    input: {
        width: "100%",
        padding: "9px 11px",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 7,
        fontSize: 13,
        fontFamily: FONT_SANS,
        background: "#FCFCFD",
        color: COLORS.text,
        boxSizing: "border-box",
    },
    inputInvalid: { borderColor: COLORS.danger, background: COLORS.dangerLight },
    errorText: { color: COLORS.danger, fontSize: 11.5, marginTop: 5 },
    monoReadonly: {
        fontFamily: FONT_MONO,
        fontSize: 12,
        background: "#F2F3F5",
        color: COLORS.textDim,
        padding: "9px 11px",
        borderRadius: 7,
        border: `1px dashed ${COLORS.border}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 8,
    },
    linkBtn: {
        border: "none",
        background: "none",
        color: COLORS.teal,
        fontSize: 11,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: FONT_SANS,
        flexShrink: 0,
    },
    note: {
        fontSize: 11.5,
        color: COLORS.textDim,
        background: "#FAFBFC",
        border: `1px dashed ${COLORS.border}`,
        padding: "9px 11px",
        borderRadius: 7,
    },
    previewThumb: {
        marginTop: 8,
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontSize: 11.5,
        color: COLORS.textDim,
    },
    previewImg: {
        width: 36,
        height: 36,
        borderRadius: 6,
        objectFit: "cover",
        border: `1px solid ${COLORS.border}`,
        background: "#F2F3F5",
    },
    row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
    toggleRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 12px",
        background: "#FAFBFC",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 7,
        marginBottom: 14,
    },
    summary: { cursor: "pointer", fontSize: 12, fontWeight: 600, color: COLORS.teal },
    pre: {
        marginTop: 8,
        background: COLORS.navy,
        color: "#D9E4EE",
        padding: 14,
        borderRadius: 8,
        fontFamily: FONT_MONO,
        fontSize: 11.5,
        overflowX: "auto",
        lineHeight: 1.6,
    },
    banner: { marginTop: 14, padding: "10px 12px", borderRadius: 7, fontSize: 12.5, fontWeight: 500 },
    bannerOk: { background: COLORS.tealLight, color: "#0B5B4F", border: "1px solid #B9E1D8" },
    bannerFail: { background: COLORS.dangerLight, color: COLORS.danger, border: "1px solid #F2C6C3" },
    actions: { display: "block", gap: 10, marginTop: 18 },
    primaryBtn: {
        background: COLORS.navy,
        color: "#fff",
        border: "none",
        padding: "11px 18px",
        borderRadius: 7,
        fontWeight: 600,
        fontSize: 13.5,
        cursor: "pointer",
        flex: 1,
    },
    ghostBtn: {
        background: "none",
        border: `1px solid ${COLORS.border}`,
        padding: "11px 14px",
        borderRadius: 7,
        fontSize: 13.5,
        fontWeight: 600,
        color: COLORS.textDim,
        cursor: "pointer",
    },
    listHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 },
    countChip: {
        background: COLORS.tealLight,
        color: "#0B5B4F",
        fontSize: 11.5,
        fontWeight: 700,
        padding: "3px 10px",
        borderRadius: 999,
    },
    emptyState: { textAlign: "center", padding: "48px 20px", color: COLORS.textDim },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 12.5 },
    th: {
        textAlign: "left",
        fontSize: 10.5,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        color: COLORS.textDim,
        fontWeight: 700,
        padding: "8px 10px",
        borderBottom: `1px solid ${COLORS.border}`,
    },
    td: { padding: "11px 10px", borderBottom: "1px solid #EEF0F3", verticalAlign: "top" },
    credBadge: {
        fontFamily: FONT_MONO,
        background: COLORS.navy,
        color: "#fff",
        padding: "3px 8px",
        borderRadius: 5,
        fontSize: 11,
        display: "inline-block",
    },
    pid: { fontFamily: FONT_MONO, fontSize: 10.5, color: COLORS.textDim, marginTop: 2 },
    statusPill: { fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 999, display: "inline-block" },
    statusActive: { background: COLORS.tealLight, color: "#0B5B4F" },
    statusInactive: { background: "#EEF0F3", color: COLORS.textDim },
    rowActionBtn: { border: "none", background: "none", color: COLORS.textDim, cursor: "pointer", fontSize: 11, fontWeight: 600 },
};